import * as R from 'ramda';
import { connect } from 'react-redux';
import React, { useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { pure, branch, compose, withHandlers, renderNothing } from 'react-recompose';
// common
import { makeSelectDocumentTemplates } from '../../../../common/selectors';
// components
import { Table } from '../../../../components/table';
import { StyledTabsMui } from '../../../../components/tabs-mui';
import { TitlePanel } from '../../../../components/title-panel';
import { EditReport } from '../../../../components/edit-report';
import { PageActions } from '../../../../components/page-actions';
import { withPromptModal } from '../../../../components/edit-report/hocs';
import { openModal, closeModal } from '../../../../components/modal/actions';
import { transformPropDataFromSelectToString } from '../../../../components/edit-report/helpers';
// features
import { AuthWrapper } from '../../../permission';
import PC from '../../../permission/role-permission';
import { startPivotTable } from '../../../pivot-table/actions';
import { makeSelectCurrentBranchGuid } from '../../../branch/selectors';
import { makeSelectInitialDataLoadedStatus } from '../../../permission/selectors';
import {
  makeSelectAllAvailableCloReferenceTypes,
  makeSelectAvailableReferenceTypesByScope,
} from '../../../reference/selectors';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// hocs
import { withFixedPopover } from '../../../../hocs';
// report-common
import { reportEnhancer } from '../../../../report-common';
// icons
import * as I from '../../../../svgs';
// ui
import { ListWrapper, IconWrapper, ZOrderWrapper } from '../../../../ui';
// utilities
import { getOrderEntryRoute } from '../../../../utilities/routes';
// feature dispatch-report
import { cloTelTableSettings } from '../../settings/table-settings';
// feature dispatch-report/order
import { getListActionsOpt, getTabs } from '../helpers';
import { withPageActionsHandlers } from '../hocs/with-page-actions-handlers';
// feature dispatch-report/order/order-by-load
import { columnSettings, FILTER_PARAMS } from './settings';
import {
  makeSelectItemList,
  makeSelectTotalCount,
  makeSelectPagination,
  makeSelectUsedReport,
  makeSelectListLoading,
  makeSelectReportStatus,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectShouldRefreshPage,
  makeSelectTableTitleFilters,
} from './selectors';
import {
  setReports,
  selectItem,
  printRequest,
  getXMLRequest,
  setUsedReport,
  cancelCLORequest,
  setTableTitleSort,
  deleteItemRequest,
  getItemListRequest,
  refreshPageRequest,
  setTableTitleFilter,
  createReportRequest,
  updateReportRequest,
  createReferenceRequest,
  resetListAndPagination,
  exportReportDataRequest,
  changeDefaultReportRequest,
} from './actions';
//////////////////////////////////////////////////

const enhance = compose(
  withFixedPopover,
  reportEnhancer,
  withPageActionsHandlers(),
  withPromptModal(FILTER_PARAMS),
  withHandlers({
    handleEditReport: (props) => (fields) => {
      const {
        openModal,
        setUsedReport,
        selectedReport,
        requestPending,
        getItemListRequest,
        createReportRequest,
        updateReportRequest,
      } = props;

      const component = (
        <EditReport
          fields={fields}
          setReport={setUsedReport}
          usedReport={selectedReport}
          requestPending={requestPending}
          createReportRequest={createReportRequest}
          updateReportRequest={updateReportRequest}
          onReportSet={() => getItemListRequest(true)}
        />
      );

      const modal = G.getDefaultReportModal(component);

      openModal(modal);
    },
  }),
  branch(
    ({ selectedReport, initialDataLoaded }) => R.or(
      R.not(initialDataLoaded),
      G.isNilOrEmpty(selectedReport),
    ),
    renderNothing,
  ),
  pure,
);

const renderTable = (props) => {
  const {
    loading,
    itemList,
    totalCount,
    selectItem,
    pagination,
    reportList,
    selectedReport,
    titleSortValues,
    tableTitleFilters,
    getItemListRequest,
    handleClickEditIcon,
    handleTableTitleFilter,
  } = props;

  if (R.not(selectedReport)) return null;

  const elementActionsComponent = (tel) => (
    <AuthWrapper has={[PC.CLO_READ, PC.CLO_WRITE]}>
      <IconWrapper px={12} cursor='pointer' onClick={(e) => handleClickEditIcon(e, tel)}>
        {I.threeDots()}
      </IconWrapper>
    </AuthWrapper>
  );

  const allChecked = G.isAllChecked(itemList);

  const tableSettings = G.getTableSettingsWithMaxHeightByConditions({
    reportList,
    selectedReport,
    tableSettings: cloTelTableSettings,
  });

  const data = {
    itemList,
    allChecked,
    totalCount,
    pagination,
    tableSettings,
    columnSettings,
    titleSortValues,
    tableTitleFilters,
    hasSelectable: true,
    useTableUiWithBg: true,
    handleTableTitleFilter,
    report: selectedReport,
    onOptionClick: selectItem,
    useSearchableColumns: true,
    withResizableColumns: true,
    useNewTitleFilterInputs: true,
    loading: R.and(loading, G.isNilOrEmpty(itemList)),
    renderRightStickedComponent: elementActionsComponent,
    filterProps: R.indexBy(
      R.prop(GC.FIELD_VALUE), transformPropDataFromSelectToString(FILTER_PARAMS),
    ),
    handleLoadMoreEntities: G.ifElse(
      loading,
      () => {},
      getItemListRequest,
    ),
  };

  return <Table {...data} />;
};

const OrderByTel = (props) => {
  const {
    itemList,
    activeTab,
    totalCount,
    handlePrint,
    getXMLRequest,
    startPivotTable,
    shouldRefreshPage,
    refreshPageRequest,
    handleSetActiveTab,
    handleAddReferenceList,
  } = props;

  const handleClickTab = (tabIndex) => {
    if (R.equals(tabIndex, 0)) G.goToRoute(GC.ROUTE_PATH_CLO_REPORT_LIST);

    if (R.equals(tabIndex, 2)) startPivotTable(GC.PIVOT_CLO_REPORT);

    handleSetActiveTab(tabIndex);
  };

  const customTitleComponent = (
    <StyledTabsMui
      count={totalCount}
      activeTab={activeTab}
      tabs={getTabs(activeTab)}
      setActiveTab={handleClickTab}
    />
  );

  const listActionsOpt = getListActionsOpt({
    handlePrint,
    getXMLRequest,
    handleAddReferenceList,
  });

  useEffect(() => {
    if (shouldRefreshPage) refreshPageRequest();
  }, [shouldRefreshPage]);

  return (
    <ListWrapper>
      <ZOrderWrapper zIndex={2}>
        <TitlePanel
          {...props}
          pt={30}
          pr={25}
          height='auto'
          withCount={true}
          popperWithCount={true}
          useTableUiWithBg={true}
          filterProps={FILTER_PARAMS}
          type={GC.CLO_BY_TEL_REPORT}
          hiddenRightFilterInfo={false}
          customTitleComponent={customTitleComponent}
          exportPermissions={R.of(Array, PC.CLO_WRITE)}
        />
      </ZOrderWrapper>
      <ZOrderWrapper zIndex={1} p='15px 25px'>
        {renderTable(props)}
      </ZOrderWrapper>
      <AuthWrapper has={[PC.CLO_WRITE]}>
        <PageActions
          listActions={listActionsOpt}
          shadowColor={G.getTheme('createButton.shadowColor')}
          count={R.filter((item) => item.selected, R.or(itemList, [])).length}
          mainAction={{
            permissions: [PC.CLO_WRITE],
            action: () => G.goToRoute(getOrderEntryRoute()),
            text: G.getWindowLocale('actions:create-clo', 'Create CLO'),
          }}
        />
      </AuthWrapper>
    </ListWrapper>
  );
};

const mapStateToProps = (state) => createStructuredSelector({
  itemList: makeSelectItemList(state),
  loading: makeSelectListLoading(state),
  totalCount: makeSelectTotalCount(state),
  pagination: makeSelectPagination(state),
  selectedReport: makeSelectUsedReport(state),
  requestPending: makeSelectReportStatus(state),
  reportList: makeSelectAvailableReports(state),
  titleSortValues: makeSelectTitleSortValues(state),
  currentBranchGuid: makeSelectCurrentBranchGuid(state),
  documentTemplates: makeSelectDocumentTemplates(state),
  shouldRefreshPage: makeSelectShouldRefreshPage(state),
  tableTitleFilters: makeSelectTableTitleFilters(state),
  refList: makeSelectAvailableReferenceTypesByScope(state),
  initialDataLoaded: makeSelectInitialDataLoadedStatus(state),
  cloRefTypes: makeSelectAllAvailableCloReferenceTypes(state),
});

export default connect(mapStateToProps, {
  openModal,
  closeModal,
  selectItem,
  setReports,
  printRequest,
  getXMLRequest,
  setUsedReport,
  startPivotTable,
  cancelCLORequest,
  setTableTitleSort,
  deleteItemRequest,
  getItemListRequest,
  refreshPageRequest,
  setTableTitleFilter,
  createReportRequest,
  updateReportRequest,
  createReferenceRequest,
  resetListAndPagination,
  exportReportDataRequest,
  changeDefaultReportRequest,
})(enhance(OrderByTel));
